// 在这里处理Canvas Tools的相关流程
import React, { useCallback, useState, useMemo, useLayoutEffect, useEffect, useRef } from 'react';
import { Layer } from 'react-konva';
import { useHotkeys } from '@mantine/hooks';
import { setTempState, getTempState, useTempState, setSharedData } from '@starflow/app/store';
import { getCanvasTools } from '@starflow/app/extension';

const getCurrentToolConfig = (currentTool) => {
  return getCanvasTools().find((item) => item.name === currentTool);
};

export const CanvasToolsHandler = React.memo(() => {
  const layerRef = useRef(null);
  const dataRef = useRef({});
  const currentCanvasTool = useTempState('currentCanvasTool');
  const [DrawingElement, setDrawingElement] = useState(null);
  const [drawingElementProps, setDrawingElementProps] = useState(null);

  const hotkeyHandlers = useMemo(() => {
    return getCanvasTools()
      .filter((item) => item.hotkey)
      .map((item) => {
        return [
          item.hotkey,
          () => {
            setTempState({ currentCanvasTool: item.name });
            item.onActive && item.onActive();
          },
        ];
      });
  }, []);

  const handleClick = useCallback(async () => {
    if (dataRef.current.drawing) return;

    const currentTool = getTempState('currentCanvasTool');
    const toolConfig = getCurrentToolConfig(currentTool);

    if (toolConfig?.handler) {
      const stage = layerRef.current.getStage();
      const pointerPos = stage.getPointerPosition();
      const scale = stage.scaleX();

      const position = {
        x: (pointerPos.x - stage.x()) / scale,
        y: (pointerPos.y - stage.y()) / scale,
      };

      const handled = await toolConfig.handler({ position });

      if (handled) {
        setTempState({ currentCanvasTool: 'select' });
      }
    }
  }, []);

  const handleMouseDown = useCallback(() => {
    const currentTool = getTempState('currentCanvasTool');
    if (!currentTool) return;

    const toolConfig = getCurrentToolConfig(currentTool);
    if (!toolConfig) return;

    if (toolConfig.drawable) {
      const stage = layerRef.current.getStage();
      const pointerPos = stage.getPointerPosition();

      setDrawingElementProps(null);
      setDrawingElement(() => toolConfig.drawingElement);

      dataRef.current.drawing = true;
      dataRef.current.points = [];
      dataRef.current.toolConfig = toolConfig;
      dataRef.current.startX = pointerPos.x;
      dataRef.current.startY = pointerPos.y;
    } else {
      setDrawingElementProps(null);
      setDrawingElement(null);
    }
  }, []);

  const handleMouseMove = useCallback(() => {
    if (dataRef.current.drawing) {
      const stage = layerRef.current.getStage();
      const pointerPos = stage.getPointerPosition();
      const scale = stage.scaleX();

      const drawingRect = {
        x: (dataRef.current.startX - stage.x()) / scale,
        y: (dataRef.current.startY - stage.y()) / scale,
        width: (pointerPos.x - dataRef.current.startX) / scale,
        height: (pointerPos.y - dataRef.current.startY) / scale,
      };

      dataRef.current.points.push((pointerPos.x - stage.x()) / scale, (pointerPos.y - stage.y()) / scale);

      setSharedData('shouldPreventStageClick', true);

      setDrawingElementProps({
        rect: drawingRect,
        points: dataRef.current.points,
        shiftPressing: getTempState('shiftPressing'),
      });
    }
  }, []);

  const handleMouseUp = useCallback(async (event) => {
    if (dataRef.current.drawing) {
      const stage = layerRef.current.getStage();
      const pointerPos = stage.getPointerPosition();
      const scale = stage.scaleX();

      const rect = {
        x: (dataRef.current.startX - stage.x()) / scale,
        y: (dataRef.current.startY - stage.y()) / scale,
        width: (pointerPos.x - dataRef.current.startX) / scale,
        height: (pointerPos.y - dataRef.current.startY) / scale,
      };

      const position = {
        x: rect.x,
        y: rect.y,
        originalX: rect.x,
        originalY: rect.y,
      };

      if (rect.width < 0) {
        rect.originalX = rect.x;
        rect.x = rect.x + rect.width;
        rect.width = -rect.width;
      }

      if (rect.height < 0) {
        rect.originalY = rect.y;
        rect.y = rect.y + rect.height;
        rect.height = -rect.height;
      }

      const handled = await dataRef.current.toolConfig?.handler?.({
        position,
        rect,
        points: dataRef.current.points,
        shiftPressing: getTempState('shiftPressing'),
        event,
      });

      if (handled) {
        setTempState({ currentCanvasTool: 'select' });
      }

      setSharedData('shouldPreventStageClick', false);

      dataRef.current.drawing = false;
      dataRef.current.points = [];
    }

    setDrawingElement(null);
    setDrawingElementProps(null);
  }, []);

  useEffect(() => {
    if (!currentCanvasTool) {
      document.body.style.cursor = 'default';
      setTempState('cursorComponent', null);
      return;
    }

    const toolConfig = getCurrentToolConfig(currentCanvasTool);

    if (!toolConfig) {
      document.body.style.cursor = 'default';
      setTempState('cursorComponent', null);
      return;
    }

    if (toolConfig.cursorComponent) {
      setTempState('cursorComponent', toolConfig.cursorComponent);
    } else if (toolConfig.cursor) {
      setTempState('cursorComponent', null);
      document.body.style.cursor = toolConfig.cursor;
    } else {
      setTempState('cursorComponent', null);
      document.body.style.cursor = 'default';
    }
  }, [currentCanvasTool]);

  useLayoutEffect(() => {
    const stage = layerRef.current.getStage();

    stage.on('click.tool', handleClick);
    stage.on('mousedown.tool', handleMouseDown);
    stage.on('mousemove.tool', handleMouseMove);
    stage.on('mouseup.tool', handleMouseUp);

    return () => {
      stage.off('click.tool');
      stage.off('mousedown.tool');
      stage.off('mousemove.tool');
      stage.off('mouseup.tool');
    };
  }, []);

  useHotkeys(hotkeyHandlers, ['INPUT', 'TEXTAREA']);

  return (
    <Layer ref={layerRef} name="canvasToolHandler">
      {DrawingElement && drawingElementProps && <DrawingElement {...drawingElementProps} />}
    </Layer>
  );
});
